// ================================================================
//  firebase.js  —  KlinicQ Forum · Firebase Configuration
//  Save at:  src/ForumFirebase/firebase.js
// ================================================================
//
//  FIREBASE CONSOLE SETUP (do ALL of these):
//  ─────────────────────────────────────────
//  1. Go to https://console.firebase.google.com
//  2. Create / open your project
//  3. Authentication → Sign-in method → Enable:
//       ✅ Google
//       ✅ Email/Password         ← THIS is what was causing the error
//       ✅ Phone
//  4. Authentication → Settings → Authorized domains → add:
//       localhost
//       klinicq.com  (your production domain)
//  5. Firestore Database → Create database → Production mode
//  6. Storage → Get started
//  7. Project Settings → Your apps → Add Web App → copy config below
//
//  npm install firebase
// ================================================================

import { initializeApp } from "firebase/app";

import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  updateProfile,
  signOut,
  onAuthStateChanged,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "firebase/auth";

import {
  getFirestore,
  collection,
  doc,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  limit,
  serverTimestamp,
  increment,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";

import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";

// ----------------------------------------------------------------
//  YOUR CONFIG — paste from Firebase Console:
//  Project Settings → Your apps → Web app → SDK setup and config
// ----------------------------------------------------------------
const firebaseConfig = {
  apiKey: "AIzaSyC472TLZNPwnUSMt2LGev0fABAvCJ8DUzQ",
  authDomain: "klinicq-forum.firebaseapp.com",
  projectId: "klinicq-forum",
  storageBucket: "klinicq-forum.firebasestorage.app",
  messagingSenderId: "426801455125",
  appId: "1:426801455125:web:8922b48d7771e4ea4a2e1c",
  measurementId: "G-PW1M2QL0W2"
};

// ----------------------------------------------------------------
//  INIT
// ----------------------------------------------------------------
const app = initializeApp(firebaseConfig);

export const auth    = getAuth(app);
export const db      = getFirestore(app);
export const storage = getStorage(app);

// ================================================================
//  AUTH — GOOGLE
// ================================================================

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: "select_account" });

/** Gmail / Google sign-in — no OTP needed */
export const signInWithGoogle = () => signInWithPopup(auth, googleProvider);

/** Sign out */
export const logOut = () => signOut(auth);

/**
 * Listen for login / logout AND page refreshes.
 * Firebase restores the session from localStorage automatically;
 * onAuthStateChanged fires with the restored user so the user
 * stays logged in after refresh without being redirected to login.
 */
export const listenToAuthState = (callback) =>
  onAuthStateChanged(auth, callback);

/** Convert Firebase user object → shape the ForumLayout component expects */
export const normaliseUser = (firebaseUser) => {
  const name =
    firebaseUser.displayName ||
    firebaseUser.email?.split("@")[0] ||
    firebaseUser.phoneNumber ||
    "User";

  const initials = name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return {
    uid:           firebaseUser.uid,
    name,
    email:         firebaseUser.email || null,
    phoneNumber:   firebaseUser.phoneNumber || null,
    photoURL:      firebaseUser.photoURL || null,
    emailVerified: firebaseUser.emailVerified,
    initials,
    bg:            "#DBEAFE",
    col:           "#1D4ED8",
  };
};

// ================================================================
//  AUTH — EMAIL + PASSWORD
//
//  Sign-Up Flow:
//    1. createUserWithEmailAndPassword  — creates account
//    2. updateProfile                   — saves displayName
//    3. sendEmailVerification           — emails the link / "OTP"
//    4. signOut                         — user must verify first
//
//  Sign-In Flow (after verification):
//    1. signInWithEmailAndPassword      — signs in
//    2. Check emailVerified             — block if not verified
//
//  WHY THE ERROR HAPPENED:
//    Firebase Console → Authentication → Sign-in method →
//    Email/Password was DISABLED. Enable it there first.
// ================================================================

/**
 * Create a new account, send verification email, then sign out.
 * The user must click the verification link before they can sign in.
 *
 * @throws Firebase auth errors (auth/email-already-in-use, auth/weak-password, etc.)
 */
export const signUpWithEmail = async (email, password, displayName) => {
  // Step 1: Create the account
  // ⚠ This will throw auth/operation-not-allowed if
  //   Email/Password is not enabled in Firebase Console
  const credential = await createUserWithEmailAndPassword(auth, email, password);

  // Step 2: Set the display name
  await updateProfile(credential.user, { displayName: displayName.trim() });

  // Step 3: Send verification email
  // Firebase sends a link to the user's inbox — they must click it
  await sendEmailVerification(credential.user, {
    // Optional: redirect URL after verification
    // url: "https://klinicq.com/forum",
    // handleCodeInApp: false,
  });

  // Step 4: Sign them out — they must verify before accessing the app
  await signOut(auth);

  return credential.user;
};

/**
 * Sign in with email and password.
 * Blocks sign-in if the email has not been verified yet.
 *
 * @throws { code: 'EMAIL_NOT_VERIFIED' } if email not verified
 * @throws Firebase auth errors (auth/wrong-password, auth/user-not-found, etc.)
 */
export const signInWithEmail = async (email, password) => {
  const credential = await signInWithEmailAndPassword(auth, email, password);

  // Block users who haven't verified their email
  if (!credential.user.emailVerified) {
    await signOut(auth);
    // Throw a custom error code so LoginScreen can show the right message
    const err = new Error("Please verify your email before signing in.");
    err.code = "EMAIL_NOT_VERIFIED";
    throw err;
  }

  return credential;
};

/**
 * Resend the verification email.
 * Signs in temporarily to get the user object, sends the email, then signs out.
 */
export const resendVerificationEmail = async (email, password) => {
  const credential = await signInWithEmailAndPassword(auth, email, password);
  await sendEmailVerification(credential.user);
  await signOut(auth);
};

// ================================================================
//  AUTH — PHONE NUMBER + SMS OTP
//
//  Flow:
//    1. Add <div id="recaptcha-container"></div> to your JSX
//    2. Call sendPhoneOtp(phoneNumber, "recaptcha-container")
//    3. User enters the 6-digit SMS code
//    4. Call verifyPhoneOtp(otpCode)
//    5. onAuthStateChanged fires → user is logged in
//
//  Console setup:
//    Authentication → Sign-in method → Phone → Enable
//    Authentication → Settings → Authorized domains → add your domain
//
//  For local testing without a real SIM:
//    Authentication → Phone → Test phone numbers
//    Add e.g. +91 9999999999 with OTP 123456
// ================================================================

/**
 * Attach an invisible reCAPTCHA to the given container div.
 * Only creates one instance — safe to call multiple times.
 */
export const setupRecaptcha = (containerId) => {
  if (window.recaptchaVerifier) return window.recaptchaVerifier;

  window.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
    size: "invisible",
    callback: () => {
      // reCAPTCHA solved — sendPhoneOtp will proceed
    },
    "expired-callback": () => {
      // reCAPTCHA expired — clear so it gets recreated next time
      clearRecaptcha();
    },
  });

  return window.recaptchaVerifier;
};

/**
 * Send an SMS OTP to phoneNumber (must be E.164 format: "+919876543210").
 * Stores the confirmationResult on window so verifyPhoneOtp can use it.
 */
export const sendPhoneOtp = async (
  phoneNumber,
  containerId = "recaptcha-container"
) => {
  const recaptchaVerifier = setupRecaptcha(containerId);
  const confirmationResult = await signInWithPhoneNumber(
    auth,
    phoneNumber,
    recaptchaVerifier
  );
  window.confirmationResult = confirmationResult;
  return confirmationResult;
};

/**
 * Verify the 6-digit OTP code entered by the user.
 * On success, Firebase signs the user in and onAuthStateChanged fires.
 */
export const verifyPhoneOtp = async (otpCode) => {
  if (!window.confirmationResult) {
    throw new Error("No OTP session found. Please request a new OTP.");
  }
  const result = await window.confirmationResult.confirm(otpCode);
  return result.user;
};

/** Clean up reCAPTCHA when leaving the phone sign-in screen */
export const clearRecaptcha = () => {
  if (window.recaptchaVerifier) {
    try {
      window.recaptchaVerifier.clear();
    } catch (_) {}
    window.recaptchaVerifier = null;
  }
  window.confirmationResult = null;
};

// ================================================================
//  USER PROFILES  →  /users/{uid}
// ================================================================

/**
 * Create a user profile document if one doesn't already exist.
 * Called every time the user logs in via onAuthStateChanged.
 */
export const upsertUserProfile = async (firebaseUser) => {
  const userRef = doc(db, "users", firebaseUser.uid);
  const snap    = await getDoc(userRef);

  if (!snap.exists()) {
    const name =
      firebaseUser.displayName ||
      firebaseUser.email?.split("@")[0] ||
      firebaseUser.phoneNumber ||
      "User";

    await setDoc(userRef, {
      name,
      email:       firebaseUser.email       || null,
      phoneNumber: firebaseUser.phoneNumber || null,
      photoURL:    firebaseUser.photoURL    || null,
      role:        "Patient",
      createdAt:   serverTimestamp(),
    });
  }
};

// ================================================================
//  POSTS  →  /posts/{postId}
// ================================================================

/** Real-time listener — fires whenever posts change */
export const listenToPosts = (callback) => {
  const q = query(collection(db, "posts"), orderBy("ts", "desc"));
  return onSnapshot(q, (snap) => {
    const posts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(posts);
  });
};

/** Create a new post */
export const createPost = async ({
  title,
  body,
  category,
  tags,
  role,
  imageUrl,
  user,
}) => {
  const docRef = await addDoc(collection(db, "posts"), {
    title,
    body:           body     || "",
    category,
    tags:           tags     || [],
    author:         user.name,
    authorId:       user.uid,
    authorInitials: user.initials,
    avatarBg:       user.bg,
    avatarCol:      user.col,
    role,
    imageUrl:       imageUrl || null,
    votes:          0,
    likedBy:        [],
    commentCount:   0,
    answered:       false,
    pinned:         false,
    ts:             serverTimestamp(),
  });
  return docRef.id;
};

/** Toggle upvote on a post */
export const toggleVote = (postId, uid, currentlyLiked) => {
  const postRef = doc(db, "posts", postId);
  return currentlyLiked
    ? updateDoc(postRef, { votes: increment(-1), likedBy: arrayRemove(uid) })
    : updateDoc(postRef, { votes: increment(1),  likedBy: arrayUnion(uid)  });
};

/** Mark a post as answered / unanswered */
export const markAnswered = (postId, answered = true) =>
  updateDoc(doc(db, "posts", postId), { answered });

/** Delete a post */
export const deletePost = (postId) =>
  deleteDoc(doc(db, "posts", postId));

// ================================================================
//  COMMENTS  →  /posts/{postId}/comments/{commentId}
// ================================================================

/** Real-time listener for comments on a specific post */
export const listenToComments = (postId, callback) => {
  const q = query(
    collection(db, "posts", postId, "comments"),
    orderBy("ts", "asc")
  );
  return onSnapshot(q, (snap) => {
    const comments = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(comments);
  });
};

/**
 * Add a comment and increment the post's commentCount atomically.
 */
export const addComment = async (postId, { text, imageUrl, parentId, user }) => {
  // Write comment
  const commentRef = await addDoc(
    collection(db, "posts", postId, "comments"),
    {
      text:      text     || "",
      imageUrl:  imageUrl || null,
      parentId:  parentId || null,
      author:    user.name,
      authorId:  user.uid,
      ts:        serverTimestamp(),
    }
  );

  // Increment commentCount on the parent post
  await updateDoc(doc(db, "posts", postId), {
    commentCount: increment(1),
  });

  return commentRef;
};

/** Delete a comment */
export const deleteComment = (postId, commentId) =>
  deleteDoc(doc(db, "posts", postId, "comments", commentId));

// ================================================================
//  COMMUNITY CHAT  →  /chat/{msgId}
// ================================================================

/** Real-time listener — last 50 messages, oldest first */
export const listenToChat = (callback) => {
  const q = query(
    collection(db, "chat"),
    orderBy("ts", "asc"),
    limit(50)
  );
  return onSnapshot(q, (snap) => {
    const messages = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(messages);
  });
};

/** Send a chat message */
export const sendChatMessage = (text, user, isDoc = false) =>
  addDoc(collection(db, "chat"), {
    text,
    from:     user.name,
    init:     user.initials,
    bg:       user.bg,
    col:      user.col,
    isDoc,
    authorId: user.uid,
    ts:       serverTimestamp(),
  });

// ================================================================
//  STORAGE — Image uploads
// ================================================================

/** Upload a post image and return its download URL */
export const uploadPostImage = async (file, uid) => {
  const fileRef = ref(
    storage,
    `posts/${uid}/${Date.now()}_${file.name}`
  );
  await uploadBytes(fileRef, file);
  return getDownloadURL(fileRef);
};

/** Upload a comment image and return its download URL */
export const uploadCommentImage = async (file, uid) => {
  const fileRef = ref(
    storage,
    `comments/${uid}/${Date.now()}_${file.name}`
  );
  await uploadBytes(fileRef, file);
  return getDownloadURL(fileRef);
};